/**
 * oMessage Theme - Main Entry Point
 */

// Import styles
import '../scss/main.scss';

// Import GSAP modules
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Import Lenis for smooth scrolling
import Lenis from 'lenis';

// Import animation modules
import { initScrollAnimations, refreshScrollTrigger } from './animations/scroll-triggers.js';
import { initPageTransitions } from './animations/page-transitions.js';
import { initHeroAnimations, initHeroParallax, animateStats } from './animations/hero-animations.js';
import { initTestimonialsScroll } from './animations/testimonials-scroll.js';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Global Lenis instance
let lenis;

/**
 * Initialize custom overlay scrollbar
 * Creates a floating scrollbar that sits on top of content
 */
function initCustomScrollbar() {
  // Create scrollbar elements
  const scrollbar = document.createElement('div');
  scrollbar.className = 'custom-scrollbar';
  scrollbar.innerHTML = `
    <div class="custom-scrollbar__track"></div>
    <div class="custom-scrollbar__thumb"></div>
  `;
  document.body.appendChild(scrollbar);

  const thumb = scrollbar.querySelector('.custom-scrollbar__thumb');
  let hideTimeout;
  let isDragging = false;
  let startY = 0;
  let startScrollY = 0;

  // Update thumb position and size
  function updateThumb() {
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const scrollY = window.scrollY;

    // Calculate thumb size (proportional to viewport/content ratio)
    const thumbHeight = Math.max(40, (clientHeight / scrollHeight) * clientHeight);

    // Calculate thumb position
    const maxScroll = scrollHeight - clientHeight;
    const scrollProgress = maxScroll > 0 ? scrollY / maxScroll : 0;
    const thumbTop = scrollProgress * (clientHeight - thumbHeight);

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.top = `${thumbTop}px`;
  }

  // Show scrollbar on scroll
  function showScrollbar() {
    scrollbar.classList.add('is-visible');
    clearTimeout(hideTimeout);
    hideTimeout = setTimeout(() => {
      if (!isDragging) {
        scrollbar.classList.remove('is-visible');
      }
    }, 1500);
  }

  // Handle scroll events
  window.addEventListener('scroll', () => {
    updateThumb();
    showScrollbar();
  }, { passive: true });

  // Handle drag start
  thumb.addEventListener('mousedown', (e) => {
    isDragging = true;
    startY = e.clientY;
    startScrollY = window.scrollY;
    thumb.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  });

  // Handle drag move
  document.addEventListener('mousemove', (e) => {
    if (!isDragging) return;

    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;
    const maxScroll = scrollHeight - clientHeight;

    const deltaY = e.clientY - startY;
    const scrollRatio = maxScroll / (clientHeight - thumb.offsetHeight);
    const newScrollY = startScrollY + (deltaY * scrollRatio);

    window.scrollTo(0, Math.max(0, Math.min(maxScroll, newScrollY)));
  });

  // Handle drag end
  document.addEventListener('mouseup', () => {
    if (isDragging) {
      isDragging = false;
      thumb.style.cursor = '';
      document.body.style.userSelect = '';

      // Hide after a delay
      hideTimeout = setTimeout(() => {
        scrollbar.classList.remove('is-visible');
      }, 1500);
    }
  });

  // Handle resize
  window.addEventListener('resize', updateThumb, { passive: true });

  // Initial update
  updateThumb();

  // Show briefly on load
  showScrollbar();
}

/**
 * Initialize Lenis smooth scrolling
 * Creates a buttery smooth scroll experience
 */
function initSmoothScroll() {
  lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential easing
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: 1,
    touchMultiplier: 2,
    infinite: false,
  });

  // Connect Lenis to GSAP's ticker for perfect sync
  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);

  // Handle anchor links with smooth scroll
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, {
          offset: -80, // Account for fixed header
          duration: 1.5,
        });
      }
    });
  });

  return lenis;
}

/**
 * Initialize header scroll behavior
 * Adds 'is-scrolled' class when user scrolls past threshold
 */
function initHeaderScroll() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const scrollThreshold = 50; // pixels

  // Check initial scroll position
  const updateHeaderState = () => {
    header.classList.toggle('is-scrolled', window.scrollY > scrollThreshold);
  };

  // Set initial state
  updateHeaderState();

  // Listen for scroll events
  window.addEventListener('scroll', updateHeaderState, { passive: true });
}

/**
 * Initialize mobile menu toggle
 */
function initMobileMenu() {
  const header = document.querySelector('.site-header');
  const toggle = document.querySelector('.site-header__mobile-toggle');
  const nav = document.querySelector('.site-header__nav');

  if (!toggle || !nav) return;

  toggle.addEventListener('click', () => {
    const isOpen = header.classList.toggle('menu-open');
    nav.classList.toggle('is-open', isOpen);
    toggle.setAttribute('aria-expanded', isOpen);

    // Prevent body scroll when menu is open
    document.body.style.overflow = isOpen ? 'hidden' : '';
  });

  // Close menu when clicking on a link
  nav.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      header.classList.remove('menu-open');
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    });
  });

  // Close menu on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && header.classList.contains('menu-open')) {
      header.classList.remove('menu-open');
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });
}

/**
 * Initialize FAQ accordion with open/close animations
 */
function initFaqAccordion() {
  const faqItems = document.querySelectorAll('.faq-item');

  faqItems.forEach((item) => {
    const summary = item.querySelector('summary');
    const answer = item.querySelector('.faq-item__answer');

    if (!summary || !answer) return;

    // Handle click for animated open/close
    summary.addEventListener('click', (e) => {
      e.preventDefault();

      const isOpen = item.hasAttribute('open');

      if (isOpen) {
        // Closing animation
        gsap.to(answer, {
          opacity: 0,
          y: -8,
          filter: 'blur(4px)',
          duration: 0.25,
          ease: 'power2.in',
          onComplete: () => {
            item.removeAttribute('open');
            // Reset styles for next open
            gsap.set(answer, { opacity: 0, y: -8, filter: 'blur(4px)' });
          }
        });
      } else {
        // Opening - set initial state, then open, then animate
        gsap.set(answer, { opacity: 0, y: -8, filter: 'blur(4px)' });
        item.setAttribute('open', '');

        gsap.to(answer, {
          opacity: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 0.4,
          ease: 'power2.out',
          delay: 0.05
        });
      }
    });
  });
}

/**
 * Initialize all animations when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  console.log('oMessage Theme initialized');

  // Initialize GSAP defaults
  gsap.defaults({
    ease: 'power2.out',
    duration: 0.6,
  });

  // Initialize Lenis smooth scrolling first
  initSmoothScroll();

  // Initialize custom overlay scrollbar
  initCustomScrollbar();

  // Initialize header scroll behavior
  initHeaderScroll();

  // Initialize mobile menu
  initMobileMenu();

  // Initialize FAQ accordion
  initFaqAccordion();

  // Initialize hero animations
  initHeroAnimations();
  initHeroParallax();
  animateStats();

  // Initialize scroll animations
  initScrollAnimations();

  // Initialize testimonials scroll
  initTestimonialsScroll();

  // Initialize page transitions
  initPageTransitions();

  // Refresh ScrollTrigger after images load
  window.addEventListener('load', () => {
    refreshScrollTrigger();
  });

  // Handle resize events
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      refreshScrollTrigger();
    }, 250);
  });
});

// Hot Module Replacement support
if (import.meta.hot) {
  import.meta.hot.accept();
}
